import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axios';

const COMPANY = {
  name: 'TYCHE ABODES PRIVATE LIMITED',
  address: 'No. 401, Ramani\'s Frazer Square, Near Head Post Office, Goods Shed Road, Coimbatore - 641001.',
  gstin: '33AAKCT5967G1ZY',
  phone: '',
  email: '',
};

const STATUS_STYLES = {
  DRAFT:      { bg:'#f0f0f0', color:'#666' },
  CONFIRMED:  { bg:'#E6F1FB', color:'#185FA5' },
  DISPATCHED: { bg:'#FAEEDA', color:'#854F0B' },
  DELIVERED:  { bg:'#EAF3DE', color:'#3B6D11' },
  CANCELLED:  { bg:'#FDE8E8', color:'#C0392B' },
};
const PAY_STYLES = {
  PENDING:  { bg:'#FDE8E8', color:'#C0392B' },
  PARTIAL:  { bg:'#FAEEDA', color:'#854F0B' },
  PAID:     { bg:'#EAF3DE', color:'#3B6D11' },
  REFUNDED: { bg:'#f0f0f0', color:'#666' },
};

const Badge = ({ text, style }) => (
  <span style={{ fontSize:11, padding:'2px 10px', borderRadius:20, fontWeight:500, ...style }}>{text?.replace('_',' ')}</span>
);

const inr = v => `₹${Number(v||0).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 })}`;

export default function SalesPage() {
  const navigate = useNavigate();
  const [tab, setTab]         = useState('orders');
  const [orders, setOrders]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [msg, setMsg]         = useState('');

  const loadOrders = useCallback(() => {
    setLoading(true);
    api.get(`/sales/orders/?search=${search}&ordering=-order_date`)
      .then(r => setOrders(r.data.results||[]))
      .finally(() => setLoading(false));
  }, [search]);

  const loadCustomers = useCallback(() => {
    api.get('/sales/customers/?page_size=200')
      .then(r => setCustomers(r.data.results||[]));
  }, []);

  useEffect(() => { loadOrders(); loadCustomers(); }, [loadOrders, loadCustomers]);

  // Dashboard stats
  const totalRevenue  = orders.reduce((s,o)=>s+parseFloat(o.total_amount||0),0);
  const totalPaid     = orders.reduce((s,o)=>s+parseFloat(o.paid_amount||0),0);
  const totalBalance  = orders.reduce((s,o)=>s+parseFloat(o.balance_amount||0),0);
  const pendingOrders = orders.filter(o=>o.payment_status!=='PAID').length;

  return (
    <Layout title="Sales" subtitle="Orders · Payments · Dispatch"
      actions={
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={()=>navigate('/sales/quotation/new')}>+ New Quotation</button>
          <button className="btn btn-primary" onClick={()=>navigate('/sales/order/new')}>+ New Order</button>
        </div>
      }
    >
      {msg&&<div className="success-box" style={{ marginBottom:16 }}>{msg}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <div className="kpi-card green"><div className="kpi-value">{inr(totalRevenue)}</div><div className="kpi-label">Total Revenue</div></div>
        <div className="kpi-card blue"><div className="kpi-value">{inr(totalPaid)}</div><div className="kpi-label">Amount Received</div></div>
        <div className="kpi-card red"><div className="kpi-value">{inr(totalBalance)}</div><div className="kpi-label">Balance Pending</div></div>
        <div className="kpi-card amber"><div className="kpi-value">{pendingOrders}</div><div className="kpi-label">Unpaid Orders</div></div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:16, border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflow:'hidden', width:'fit-content' }}>
        {[['orders','Sales Orders'],['customers','Customers']].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{ padding:'8px 20px', fontSize:13, fontWeight:500, background:tab===t?'var(--brand)':'var(--white)', color:tab===t?'#fff':'var(--text-secondary)', border:'none', cursor:'pointer', borderRight:'1px solid var(--border)' }}>{l}</button>
        ))}
      </div>

      {/* ORDERS TAB */}
      {tab==='orders' && (
        <div className="card">
          <div className="filter-bar">
            <input className="search-input" placeholder="Search order, customer..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:1, padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', fontSize:13 }}/>
          </div>
          {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
          orders.length===0 ? <div className="empty-state"><p>No orders yet.</p></div> : (
            <div style={{ overflowX:'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o,i)=>(
                    <tr key={i}>
                      <td style={{ fontFamily:'DM Mono,monospace', fontWeight:600, color:'var(--brand)' }}>{o.order_number}</td>
                      <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{o.order_date}</td>
                      <td style={{ fontWeight:500 }}>{o.customer_name}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center' }}>{o.item_count}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontWeight:600 }}>{inr(o.total_amount)}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--green)' }}>{inr(o.paid_amount)}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--red)' }}>{inr(o.balance_amount)}</td>
                      <td><Badge text={o.status} style={STATUS_STYLES[o.status]||{}}/></td>
                      <td><Badge text={o.payment_status} style={PAY_STYLES[o.payment_status]||{}}/></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-sm btn-secondary" onClick={()=>navigate(`/sales/order/${o.id}`)}>View</button>
                          <button className="btn btn-sm btn-primary" onClick={()=>navigate(`/sales/invoice/${o.id}`)}>Invoice</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CUSTOMERS TAB */}
      {tab==='customers' && (
        <div className="card">
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <button className="btn btn-primary btn-sm" onClick={()=>navigate('/sales/customer/new')}>+ Add Customer</button>
          </div>
          {customers.length===0 ? <div className="empty-state"><p>No customers yet.</p></div> : (
            <div style={{ overflowX:'auto' }}>
              <table>
                <thead>
                  <tr><th>Name</th><th>Type</th><th>Phone</th><th>City</th><th>GSTIN</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {customers.map((c,i)=>(
                    <tr key={i}>
                      <td style={{ fontWeight:600 }}>{c.name}</td>
                      <td><span className="badge badge-blue">{c.customer_type}</span></td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{c.phone}</td>
                      <td style={{ fontSize:12 }}>{c.city||'—'}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:11 }}>{c.gstin||'—'}</td>
                      <td><button className="btn btn-sm btn-secondary" onClick={()=>navigate(`/sales/customer/${c.id}`)}>Edit</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
